import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, GraduationCap, Calendar, Clock, CheckCircle, AlertCircle, ArrowLeft, Globe, Users, BookOpen, Award, Home, Trophy, ShoppingBag, Code } from 'lucide-react';
import { Event, Formation, FormationsByYear } from '../types/index';
import { apiService } from '../services/api';

const FormationsPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [, setSelectedFormation] = useState<Formation | null>(null);
  const [formations, setFormations] = useState<FormationsByYear>({});
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchFormations();
  }, []);

  const fetchFormations = async () => {
    try {
      const response = await apiService.getEvents();
      if (response.success && response.data) {
        const events: Event[] = response.data;
        const formationsData = events.filter((event: Event) => event.type === 'FORMATION');

        // Organiser par année
        const formationsByYear: FormationsByYear = {};
        const years = new Set<number>();

        formationsData.forEach((formation: Event) => {
          const date = new Date(formation.dateStart);
          const year = date.getFullYear();
          years.add(year);

          if (!formationsByYear[year]) {
            formationsByYear[year] = [];
          }

          // Vérifier si la formation est terminée
          const now = new Date();
          const endDate = formation.dateEnd ? new Date(formation.dateEnd) : date;
          const isExpired = endDate < now;

          // Adapter les données pour correspondre au format attendu
          const adaptedFormation: Formation = {
            id: formation.id,
            date: date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/\./g, '').toUpperCase(),
            title: formation.title,
            level: formation.category || 'Tous niveaux',
            duration: '1 jour', // Valeur par défaut
            price: `${formation.price || 0}€`,
            spots: (formation.maxParticipants || 20) - formation.registeredParticipants,
            maxSpots: formation.maxParticipants || 20,
            description: formation.description,
            topics: formation.technologies || [],
            instructor: formation.instructor || 'Équipe Phoelynx',
            location: formation.location,
            dateStart: formation.dateStart,
            dateEnd: formation.dateEnd,
            maxParticipants: formation.maxParticipants || 20,
            registeredParticipants: formation.registeredParticipants,
            status: formation.status,
            technologies: formation.technologies || [],
            registrationUrl: formation.registrationUrl,
            registrationOpen: formation.registrationOpen,
            hidePricing: formation.hidePricing,
            isExpired: isExpired
          };

          formationsByYear[year].push(adaptedFormation);
        });

        // Trier les formations par date dans chaque année
        Object.keys(formationsByYear).forEach((yearKey: string) => {
          const yearNum = parseInt(yearKey);
          formationsByYear[yearNum].sort((a: Formation, b: Formation) =>
            new Date(a.dateStart).getTime() - new Date(b.dateStart).getTime()
          );
        });

        // Trier les années par ordre chronologique (plus ancienne en premier, récentes à droite)
        const sortedYears = Array.from(years).sort((a, b) => a - b);
        setAvailableYears(sortedYears);

        setFormations(formationsByYear);

        // Définir 2025 par défaut si disponible, sinon la plus récente
        const currentYear = 2025;
        if (sortedYears.includes(currentYear)) {
          setSelectedYear(currentYear);
        } else if (sortedYears.length > 0) {
          // Prendre l'année la plus récente
          setSelectedYear(sortedYears[sortedYears.length - 1]);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const navigation = [
    { path: '/', label: 'Accueil', icon: Home },
    { path: '/formations', label: 'Formations', icon: GraduationCap },
    { path: '/competitions', label: 'Compétitions', icon: Trophy },
    { path: '/projets', label: 'Projets', icon: Code },
    { path: '/boutique', label: 'Boutique', icon: ShoppingBag }
  ];


  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-lg py-3 shadow-lg shadow-black/50' : 'bg-gradient-to-b from-black/80 to-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/assets/logos/Phoelynx_Transparent.webp"
              alt="Phoelynx France"
              className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:brightness-110 drop-shadow-lg group-hover:drop-shadow-xl"
            />
            <span className="lg:text-2xl md:text-xl sm:text-lg font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">
              Phoelynx France
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center lg:space-x-8 md:space-x-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-1 lg:space-x-2 lg:px-4 md:px-2 py-2 rounded-lg transition-all hover:bg-white/10 ${
                    item.path === '/formations' ? 'bg-red-500/20 text-red-300' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="lg:text-base md:text-sm">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-white/10">
            <div className="flex flex-col space-y-2 pt-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all hover:bg-white/10 ${
                      item.path === '/formations' ? 'bg-red-500/20 text-red-300' : 'text-gray-300 hover:text-white'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </nav>

      {/* Header */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-red-500/20 to-sky-500/20">
              <GraduationCap className="w-8 h-8 text-sky-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent">FORMATIONS</h1>
          </div>

          <p className="text-gray-400 text-lg mb-8">
            Développez vos compétences avec nos formations pratiques en robotique, programmation et technologies avancées
          </p>
        </div>
      </section>

      {/* Calendrier des Formations */}
      <section className="pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
              <span className="ml-4 text-gray-400">Chargement des formations...</span>
            </div>
          ) : Object.keys(formations).length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">Aucune formation disponible</h3>
              <p className="text-gray-500">Les formations seront bientôt disponibles.</p>
            </div>
          ) : (
            <>
              {/* Year Selector */}
              <div className="flex justify-center gap-4 mb-12">
                {availableYears.map((year) => (
                  <button
                    key={year}
                    onClick={() => setSelectedYear(year)}
                    className={`px-6 py-3 rounded-full font-semibold transition-all ${
                      selectedYear === year
                        ? 'bg-gradient-to-r from-red-500 to-sky-500 text-white shadow-lg shadow-sky-500/30'
                        : 'bg-gray-900 text-gray-400 hover:bg-gray-800 border border-gray-700'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>

              {/* Formations Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {formations[selectedYear]?.map((formation: Formation) => (
              <div
                key={formation.id}
                className="group bg-gradient-to-br from-gray-900/80 to-gray-900/40 rounded-xl overflow-hidden border border-gray-800 hover:border-sky-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-sky-500/10"
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-3">
                        <span className="text-sm px-3 py-1 bg-gradient-to-r from-red-500/20 to-sky-500/20 text-sky-300 rounded-full flex items-center gap-1 border border-sky-500/30">
                          <Calendar className="w-3 h-3" />
                          {formation.date}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          formation.level === 'Débutant' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                          formation.level === 'Intermédiaire' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                          formation.level === 'Avancé' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                          'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        }`}>
                          {formation.level}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent">{formation.price}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                        <Clock className="w-3 h-3" />
                        {formation.duration}
                      </p>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold mb-2 group-hover:text-sky-400 transition-colors">
                    {formation.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">{formation.description}</p>

                  {/* Instructor & Location */}
                  <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {formation.instructor}
                    </span>
                    <span>{formation.location}</span>
                  </div>

                  {/* Topics */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {formation.topics.map((topic: string, idx: number) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-gray-800/50 rounded-full text-gray-300 border border-gray-700">
                        {topic}
                      </span>
                    ))}
                  </div>

                  {/* Places disponibles */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Places disponibles</span>
                      <span className={`font-semibold ${
                        formation.spots === 0 ? 'text-red-400' :
                        formation.spots <= 3 ? 'text-yellow-400' :
                        'text-green-400'
                      }`}>
                        {formation.spots === 0 ? 'COMPLET' : `${formation.spots}/${formation.maxSpots}`}
                      </span>
                    </div>
                    <div className="w-full bg-gray-800/50 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          formation.spots === 0 ? 'bg-gradient-to-r from-red-400 to-red-600' :
                          formation.spots <= 3 ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                          'bg-gradient-to-r from-green-400 to-green-600'
                        }`}
                        style={{ width: `${((formation.maxSpots - formation.spots) / formation.maxSpots) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Action Button */}
                  <button
                    className={`w-full mt-4 py-3 rounded-full font-semibold transition-all ${
                      formation.spots === 0 || formation.isExpired
                        ? 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700'
                        : 'bg-gradient-to-r from-red-500 to-sky-500 text-white hover:shadow-lg hover:shadow-sky-500/30 transform hover:scale-[1.02]'
                    }`}
                    disabled={formation.spots === 0 || formation.isExpired}
                    onClick={() => setSelectedFormation(formation)}
                  >
                    {formation.isExpired ?
                      <span className="flex items-center justify-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        FORMATION TERMINÉE
                      </span> :
                      formation.spots === 0 ?
                        <span className="flex items-center justify-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          COMPLET - Liste d'attente
                        </span> :
                        <span className="flex items-center justify-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          S'INSCRIRE
                        </span>
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
          </>
          )}

          {/* Call to Action */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center bg-gradient-to-r from-red-900/10 to-sky-900/10 rounded-2xl p-8 border border-sky-500/30 backdrop-blur-sm">
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-red-500/20 to-sky-500/20 mb-4">
                <BookOpen className="w-6 h-6 text-sky-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-red-400 to-sky-400 bg-clip-text text-transparent">
                Formation sur mesure
              </h3>
              <p className="text-gray-400 mb-6">Formations personnalisées pour entreprises et établissements scolaires</p>
              <button className="px-8 py-3 bg-white text-black font-semibold rounded-full hover:bg-gray-200 transition-all shadow-lg">
                DEMANDER UN PROGRAMME
              </button>
            </div>

            <div className="text-center bg-gradient-to-r from-sky-900/10 to-red-900/10 rounded-2xl p-8 border border-red-500/30 backdrop-blur-sm">
              <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-sky-500/20 to-red-500/20 mb-4">
                <Award className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-sky-400 to-red-400 bg-clip-text text-transparent">
                Certifications
              </h3>
              <p className="text-gray-400 mb-6">Obtenez des certifications reconnues dans le domaine de la robotique</p>
              <button className="px-8 py-3 bg-gradient-to-r from-red-500 to-sky-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-red-500/25 transition-all">
                EN SAVOIR PLUS
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-3">
              <img
                src="/assets/logos/Phoelynx_Transparent.webp"
                alt="Phoelynx France"
                className="w-8 h-8 object-contain"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">
                Phoelynx France
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Globe className="w-5 h-5 text-gray-400 hover:text-red-400 cursor-pointer transition-colors" />
              <span className="text-gray-400">phoelynx.fr</span>
            </div>

            <div className="flex flex-col items-center md:items-end gap-2">
              <p className="text-gray-400 text-sm">© 2025-{new Date().getFullYear()} Phoelynx France. Tous droits réservés.</p>
              <div className="flex gap-4 text-xs">
                <Link to="/mentions-legales" className="text-gray-500 hover:text-red-400 transition-colors">
                  Mentions légales
                </Link>
                <Link to="/politique-confidentialite" className="text-gray-500 hover:text-red-400 transition-colors">
                  Confidentialité
                </Link>
                <Link to="/conditions-utilisation" className="text-gray-500 hover:text-red-400 transition-colors">
                  CGU
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FormationsPage;